import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, createTextVNode as _createTextVNode, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "w-full space-y-4" }
const _hoisted_2 = { class: "font-semibold text-xl text-center" }
const _hoisted_3 = { class: "flex justify-center" }
const _hoisted_4 = { class: "max-w-[80%] w-full" }
const _hoisted_5 = { class: "text-center pb-2 font-semibold" }
const _hoisted_6 = { class: "flex justify-end pb-2" }
const _hoisted_7 = {
  key: 0,
  class: "pt-2 text-center text-sm text-amber-600"
}
const _hoisted_8 = { class: "flex justify-center" }

import { computed, watch } from 'vue';
import CheckboxTree from '@/components/common/CheckboxTree.vue';
import { Button } from '@/components/ui/button';
import { defaultReportFarmIds } from '@/composables/inventory/inventoryReport';
import { useInventoryScope } from '@/composables/inventory/useInventoryScope';
import { MEDICINE_REPORT_MAX_FARMS, type CheckboxTreeNode } from '@/types';


export default /*@__PURE__*/_defineComponent({
  __name: 'SelectFarmsStep',
  props: {
    "value": { required: true },
    "valueModifiers": {},
  },
  emits: /*@__PURE__*/_mergeModels(["next"], ["update:value"]),
  setup(__props) {

const farmIds = _useModel<number[]>(__props, 'value');



/** Every farm selected in the dashboard's location picker is offered; the dashboard's own selection is never written from here. */
const { selectedFarms: offeredFarms } = useInventoryScope();

const atLimit = computed(() => farmIds.value.length >= MEDICINE_REPORT_MAX_FARMS);

/** The cap is expressed as `disabled` rather than enforced on click, so the tree cannot hand back an over-long selection; a ticked farm is never disabled, so the last pick can be undone. */
const nodes = computed<CheckboxTreeNode[]>(() =>
    offeredFarms.value.map((farm) => ({
        key: String(farm.farm_id),
        label: farm.name,
        disabled: atLimit.value && !farmIds.value.includes(farm.farm_id),
    }))
);
const selectedKeys = computed(() => farmIds.value.map(String));

function applySelection(keys: string[]) {
    farmIds.value = keys.map(Number);
}

/** As many farms as the cap allows, first in the list first; past the cap "all" can never be ticked, so the button says where it stops. */
const selectableCount = computed(() => Math.min(offeredFarms.value.length, MEDICINE_REPORT_MAX_FARMS));
const isAllSelected = computed(() => selectableCount.value > 0 && farmIds.value.length >= selectableCount.value);

function toggleAll() {
    farmIds.value = isAllSelected.value ? [] : defaultReportFarmIds(offeredFarms.value);
}

// A fresh wizard starts with the first farms of the selection ticked; coming back from the next step keeps what was ticked.
// Watched rather than read once on mount: on a direct page load the farm list arrives after this step is created.
// A farm the location picker no longer offers is dropped from the ticked ones, so it is never sent unseen.
watch(
    offeredFarms,
    (farms) => {
        const offered = new Set(farms.map((farm) => farm.farm_id));
        if (farmIds.value.some((id) => !offered.has(id))) {
            farmIds.value = farmIds.value.filter((id) => offered.has(id));
        }
        if (farmIds.value.length === 0 && farms.length > 0) {
            farmIds.value = defaultReportFarmIds(farms);
        }
    },
    { immediate: true, flush: 'post' }
);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("h2", _hoisted_2, _toDisplayString(_ctx.$t('views.health.inventory.report.selectFarms.title')), 1),
    _createElementVNode("div", _hoisted_3, [
      _createElementVNode("div", _hoisted_4, [
        _createElementVNode("h3", _hoisted_5, _toDisplayString(_ctx.$t('views.health.inventory.report.selectFarms.description', { max: _unref(MEDICINE_REPORT_MAX_FARMS) })), 1),
        _createElementVNode("div", _hoisted_6, [
          _createVNode(_unref(Button), {
            variant: "outline",
            size: "sm",
            disabled: selectableCount.value === 0,
            title: 
                            !isAllSelected.value && _unref(offeredFarms).length > _unref(MEDICINE_REPORT_MAX_FARMS) ? _ctx.$t('views.health.inventory.report.selectFarms.selectAllLimit', { max: _unref(MEDICINE_REPORT_MAX_FARMS) }) : undefined
                        ,
            onClick: toggleAll
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(isAllSelected.value ? _ctx.$t('global.uiElements.multiSelect.deselectAllButtonLabel') : _ctx.$t('global.uiElements.multiSelect.selectAllButtonLabel')), 1)
            ]),
            _: 1
          }, 8, ["disabled", "title"])
        ]),
        _createElementVNode("div", null, [
          _createVNode(CheckboxTree, {
            nodes: nodes.value,
            "selected-keys": selectedKeys.value,
            "empty-label": _ctx.$t('views.health.inventory.report.selectFarms.noFarms'),
            "onUpdate:selectedKeys": applySelection
          }, null, 8, ["nodes", "selected-keys", "empty-label"])
        ]),
        (atLimit.value)
          ? (_openBlock(), _createElementBlock("p", _hoisted_7, _toDisplayString(_ctx.$t('views.health.inventory.report.selectFarms.limitReached', { max: _unref(MEDICINE_REPORT_MAX_FARMS) })), 1))
          : _createCommentVNode("", true)
      ])
    ]),
    _createElementVNode("div", _hoisted_8, [
      _createVNode(_unref(Button), {
        disabled: farmIds.value.length === 0,
        class: "min-w-32",
        onClick: _cache[0] || (_cache[0] = ($event: any) => (_ctx.$emit('next')))
      }, {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(_ctx.$t('views.reports.nextButtonLabel')), 1)
        ]),
        _: 1
      }, 8, ["disabled"])
    ])
  ]))
}
}

})